import { parsePhoneParts } from './masks.js';
import { resolveChargedAmount, resolveProcessingCostAmount } from './processingCost.js';

const CURRENCY = 'BRL';

const firedKeys = new Set();

export function trackBeginCheckout(config, formData) {
  pushEvent('begin_checkout', {
    ...buildDonationParams(config, formData),
    ecommerce: buildEcommerce(config, formData)
  });
}

export function trackLead(config, formData, submissionId) {
  pushEvent('generate_lead', {
    ...buildDonationParams(config, formData),
    user_data: buildUserData(formData.donorInfo)
  }, `generate_lead:${submissionId}`);
}

export function trackPaymentInfo(config, formData) {
  const paymentType = toSnakeCase(formData.paymentMethod);

  pushEvent('add_payment_info', {
    ...buildDonationParams(config, formData),
    payment_type: paymentType,
    user_data: buildUserData(formData.donorInfo),
    ecommerce: { ...buildEcommerce(config, formData), payment_type: paymentType }
  });
}

export function trackPurchase(config, formData, paymentStatus) {
  pushEvent('purchase', {
    ...buildDonationParams(config, formData),
    payment_type: toSnakeCase(formData.paymentMethod),
    payment_status: paymentStatus,
    user_data: buildUserData(formData.donorInfo),
    ecommerce: { ...buildEcommerce(config, formData), transaction_id: formData.submissionId }
  }, `purchase:${formData.submissionId}`);
}

export function trackPaymentFailed(config, formData, chargeStatus) {
  pushEvent('payment_failed', {
    ...buildDonationParams(config, formData),
    payment_type: toSnakeCase(formData.paymentMethod),
    charge_status: toSnakeCase(chargeStatus)
  });
}

function pushEvent(event, params, dedupKey = null) {
  if (typeof window === 'undefined' || !Array.isArray(window.dataLayer)) return;
  if (dedupKey && firedKeys.has(dedupKey)) return;

  try {
    if (params.ecommerce) {
      window.dataLayer.push({ ecommerce: null });
    }
    if (dedupKey) firedKeys.add(dedupKey);
    window.dataLayer.push({ event, ...params });
  } catch {
    firedKeys.delete(dedupKey);
  }
}

export function buildDonationParams(config, formData) {
  const base = {
    tm_form_id: config.AcquisitionFormId || null,
    tm_campaign_id: config.CampaignId || null,
    tm_form_type: config.Behaviour?.Goal === 'Lead' ? 'lead' : 'donation'
  };

  if (base.tm_form_type === 'lead') return base;

  return {
    ...base,
    value: centsToDecimal(resolveChargedAmount(config, formData)),
    currency: CURRENCY,
    donation_type: resolveDonationType(formData),
    frequency: toSnakeCase(formData.frequencyId)
  };
}

export function buildEcommerce(config, formData) {
  const donationType = resolveDonationType(formData);
  const cartLines = (formData.selection || []).filter(entry => entry.projectId || entry.customFieldKey);
  const processingCostAmount = resolveProcessingCostAmount(config, formData);
  const donationItems = cartLines.length > 0
    ? cartLines.map(entry => toEcommerceItem(entry, donationType))
    : [{
      item_id: config.CampaignId || config.AcquisitionFormId || 'donation',
      item_name: 'donation',
      item_category: donationType,
      price: centsToDecimal(formData.amount),
      quantity: 1
    }];
  const items = processingCostAmount
    ? [...donationItems, {
      item_id: 'processing_cost',
      item_name: 'processing_cost',
      item_category: donationType,
      price: centsToDecimal(processingCostAmount),
      quantity: 1
    }]
    : donationItems;

  return {
    value: centsToDecimal(resolveChargedAmount(config, formData)),
    currency: CURRENCY,
    items
  };
}

// A project line is one item at its own amount; an ItemPicker line carries its
// unit price and quantity, so GA4 sees how many of each item went in the cart.
function toEcommerceItem(entry, donationType) {
  if (entry.customFieldKey) {
    return {
      item_id: entry.customFieldKey,
      item_name: entry.label || entry.customFieldKey,
      item_category: donationType,
      price: centsToDecimal(entry.unitAmount),
      quantity: entry.quantity
    };
  }

  return {
    item_id: entry.projectId,
    item_name: entry.projectLabel || entry.cardTitle || entry.projectId,
    item_category: donationType,
    price: centsToDecimal(entry.amount),
    quantity: 1
  };
}

export function buildUserData(donorInfo) {
  const email = String(donorInfo?.Email || '').trim().toLowerCase();
  const phone = donorInfo?.Phone ? toE164(donorInfo.Phone, donorInfo.PhoneCountry) : null;

  return {
    ...(email ? { email } : {}),
    ...(phone ? { phone_number: phone } : {})
  };
}

function toE164(rawPhone, country) {
  const { countryCode, areaCode, number } = parsePhoneParts(rawPhone, country);
  const digits = `${countryCode}${areaCode}${number}`.replace(/\D/g, '');
  return digits ? `+${digits}` : null;
}

function toSnakeCase(value) {
  return value ? String(value).replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase() : null;
}

function resolveDonationType(formData) {
  return formData.frequencyId === 'OneTime' ? 'one_time' : 'recurring';
}

function centsToDecimal(cents) {
  return Math.round(Number(cents || 0)) / 100;
}
